import { useMemo, useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import { COLORS, CATEGORIES, CATEGORY_LABELS } from "@/data/colors";

function ColorTile({ c, name }) {
  return (
    <div
      data-testid={`color-tile-${c.code}`}
      className="group relative flex flex-col border-r border-b border-white/10 hover:bg-white/[0.02] transition-colors"
    >
      <div
        className="aspect-[5/3] w-full transition-transform duration-700 group-hover:scale-[1.01] origin-center"
        style={{ background: c.swatch }}
        aria-hidden
      />
      <div className="flex items-baseline gap-3 px-4 py-3">
        <span className="font-display text-[10px] tracking-[0.18em] text-white/40 tabular-nums">
          {c.code}
        </span>
        <span className="text-xs text-white/85 leading-tight truncate">
          {name}
        </span>
      </div>
    </div>
  );
}

export default function ColorShowcase() {
  const { t, lang } = useT();
  const [filter, setFilter] = useState("all");

  const labels = CATEGORY_LABELS[lang] || CATEGORY_LABELS.en;

  const visible = useMemo(() => {
    if (filter === "all") return COLORS;
    return COLORS.filter((c) => c.category === filter);
  }, [filter]);

  return (
    <section
      id="finishes"
      data-testid="finishes-section"
      className="relative bg-[#0A0A0A] border-b border-white/10"
    >
      <div className="grid grid-cols-12 border-b border-white/10">
        <div className="col-span-12 md:col-span-3 border-r border-white/10 px-6 sm:px-8 py-10 md:py-20">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50">
            {t.finishes.label}
          </p>
        </div>
        <div className="col-span-12 md:col-span-9 px-6 sm:px-10 py-10 md:py-20">
          <h2
            data-testid="finishes-headline"
            className="font-display font-bold uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
          >
            {t.finishes.headlineL1}
            {t.finishes.headlineL2 && (
              <>
                <br />
                <span className="text-white/40">{t.finishes.headlineL2}</span>
              </>
            )}
          </h2>
          <p className="mt-6 text-base text-white/70 max-w-xl">
            {t.finishes.intro}
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-white/10 overflow-x-auto">
        <div className="flex items-stretch min-w-max">
          {[{ key: "all" }, ...CATEGORIES.map((k) => ({ key: k }))].map((f, i) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`finish-filter-${f.key}`}
                className={`relative px-5 sm:px-6 py-4 text-[10px] uppercase tracking-[0.28em] transition-colors border-r border-white/10 ${
                  active
                    ? "text-white bg-white/[0.06]"
                    : "text-white/45 hover:text-white"
                }`}
              >
                {labels[f.key] || f.key}
                {active && (
                  <span className="absolute left-0 right-0 bottom-0 h-px bg-white" />
                )}
              </button>
            );
          })}
          <div className="ml-auto px-5 sm:px-6 py-4 text-[10px] uppercase tracking-[0.28em] text-white/40">
            {visible.length} / {COLORS.length}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 border-l border-t border-white/10">
        {visible.map((c) => (
          <ColorTile key={c.code} c={c} name={c.names[lang] || c.names.en} />
        ))}
      </div>
    </section>
  );
}
